import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  MagnifyingGlass, ArrowsClockwise, FileText, Warning, CheckCircle,
  Funnel, ArrowRight,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_LABELS = {
  name: 'Produktnavn',
  description: 'Produktbeskrivelse',
  short_description: 'Kort beskrivelse',
  meta_title: 'Meta-tittel',
  meta_description: 'Meta-beskrivelse',
};

const PRODUCT_FIELDS = ['description', 'short_description', 'meta_title', 'meta_description', 'name'];
const CATEGORY_FIELDS = ['description', 'meta_title', 'meta_description', 'name'];
const TERM_FIELDS = ['description', 'meta_title', 'meta_description'];

export default function CleanupPage() {
  const getHeaders = useCallback(() => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  return (
    <div className="space-y-6" data-testid="cleanup-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-extrabold flex items-center gap-2 sm:gap-3">
          <MagnifyingGlass size={28} weight="bold" className="sm:hidden" />
          <MagnifyingGlass size={36} weight="bold" className="hidden sm:inline" /> Innholdssjekk
        </h1>
        <p className="text-xs sm:text-sm text-muted-foreground mt-1">
          Bulk-søk og erstatt tekst på tvers av produkter, kategorier og termer — pluss audit av PDF-oppskrifter.
        </p>
      </div>

      <Tabs defaultValue="search" className="space-y-4">
        <div className="overflow-x-auto -mx-1 px-1 pb-1 [-webkit-overflow-scrolling:touch]">
          <TabsList className="w-max">
            <TabsTrigger value="search" className="gap-1" data-testid="tab-search">
              <MagnifyingGlass size={14} /> Søk & Erstatt
            </TabsTrigger>
            <TabsTrigger value="pdf-audit" className="gap-1" data-testid="tab-pdf-audit">
              <FileText size={14} /> PDF-audit
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="search">
          <SearchAndReplace getHeaders={getHeaders} />
        </TabsContent>

        <TabsContent value="pdf-audit">
          <PdfAudit getHeaders={getHeaders} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Søk & Erstatt ─────────────────────────────────────────────────
function SearchAndReplace({ getHeaders }) {
  const [scope, setScope] = useState('products');
  const [query, setQuery] = useState('');
  const [replaceWith, setReplaceWith] = useState('');
  const [wholeWord, setWholeWord] = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [fields, setFields] = useState(['description', 'short_description', 'meta_title', 'meta_description']);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [leverandorFilter, setLeverandorFilter] = useState('');
  const [filters, setFilters] = useState({ categories: [], leverandor: [] });
  const [results, setResults] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searching, setSearching] = useState(false);
  const [replacing, setReplacing] = useState(false);

  const availableFields = scope === 'products' ? PRODUCT_FIELDS : scope === 'categories' ? CATEGORY_FIELDS : TERM_FIELDS;

  // Load filter options once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/cleanup/filters`, { headers: getHeaders() });
        setFilters(data);
      } catch {
        // ignore
      }
    })();
  }, [getHeaders]);

  const toggleField = (f) => {
    setFields((prev) => prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]);
  };

  const runSearch = async () => {
    if (!query.trim()) { toast.error('Skriv inn et søkeord'); return; }
    if (fields.length === 0) { toast.error('Velg minst ett felt'); return; }
    setSearching(true);
    setResults(null);
    setSelectedIds(new Set());
    try {
      const body = {
        query, fields, whole_word: wholeWord, case_sensitive: caseSensitive, scope,
        category_ids: scope === 'products' && categoryFilter ? [parseInt(categoryFilter, 10)] : null,
        leverandor_terms: scope === 'products' && leverandorFilter ? [leverandorFilter] : null,
      };
      const { data } = await axios.post(`${API}/api/cleanup/search`, body, { headers: getHeaders() });
      setResults(data);
      // Preselect all results
      const ids = scope === 'terms'
        ? data.results.map((r) => `${r.attribute_id}:${r.term_id}`)
        : data.results.map((r) => r.id);
      setSelectedIds(new Set(ids));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Søk feilet');
    } finally {
      setSearching(false);
    }
  };

  const runReplace = async () => {
    if (!results || selectedIds.size === 0) return;
    if (!window.confirm(`Erstatte "${query}" med "${replaceWith}" på ${selectedIds.size} ${scope}?`)) return;
    setReplacing(true);
    try {
      const body = {
        query, replace_with: replaceWith,
        whole_word: wholeWord, case_sensitive: caseSensitive,
        scope, fields,
        target_ids: scope === 'terms' ? Array.from(selectedIds) : Array.from(selectedIds).map((x) => parseInt(x, 10)),
      };
      const { data } = await axios.post(`${API}/api/cleanup/replace`, body, { headers: getHeaders() });
      toast.success(data.message);
      // Re-run search to show what's left
      await runSearch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erstatning feilet');
    } finally {
      setReplacing(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {/* Search form */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Funnel size={16} /> Søkeparametere</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Søk etter</Label>
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="f.eks. bergen" data-testid="cleanup-query" />
            </div>
            <div>
              <Label>Erstatt med (la stå tom for å bare søke)</Label>
              <Input value={replaceWith} onChange={(e) => setReplaceWith(e.target.value)} placeholder="(tom)" data-testid="cleanup-replace" />
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <Checkbox checked={wholeWord} onCheckedChange={(v) => setWholeWord(!!v)} /> Kun hele ord
            </label>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <Checkbox checked={caseSensitive} onCheckedChange={(v) => setCaseSensitive(!!v)} /> Skill store/små bokstaver
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Søk i</Label>
              <Select value={scope} onValueChange={(v) => { setScope(v); setResults(null); }}>
                <SelectTrigger data-testid="cleanup-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="products">Produkter</SelectItem>
                  <SelectItem value="categories">Kategorier</SelectItem>
                  <SelectItem value="terms">Attributt-termer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {scope === 'products' && (
              <>
                <div>
                  <Label>Filter: kategori</Label>
                  <Select value={categoryFilter || 'ALL'} onValueChange={(v) => setCategoryFilter(v === 'ALL' ? '' : v)}>
                    <SelectTrigger data-testid="cleanup-category"><SelectValue placeholder="Alle kategorier" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Alle kategorier</SelectItem>
                      {filters.categories.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>{c.name} ({c.count || 0})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Filter: leverandør</Label>
                  <Select value={leverandorFilter || 'ALL'} onValueChange={(v) => setLeverandorFilter(v === 'ALL' ? '' : v)}>
                    <SelectTrigger data-testid="cleanup-leverandor"><SelectValue placeholder="Alle leverandører" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Alle leverandører</SelectItem>
                      {filters.leverandor.map((l) => (
                        <SelectItem key={l.slug} value={l.name}>{l.name} ({l.count || 0})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          <div>
            <Label>Felter å søke i</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              {availableFields.map((f) => (
                <label key={f} className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid={`field-${f}`}>
                  <Checkbox checked={fields.includes(f)} onCheckedChange={() => toggleField(f)} />
                  {FIELD_LABELS[f] || f}
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={runSearch} disabled={searching} className="gap-2" data-testid="cleanup-search-btn">
              <MagnifyingGlass size={14} className={searching ? 'animate-pulse' : ''} />
              {searching ? 'Søker...' : 'Søk'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between gap-2 flex-wrap">
              <span>{results.total} treff{results.truncated ? ' (begrenset til 500)' : ''}</span>
              {replaceWith && results.total > 0 && (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>Fjern alle</Button>
                  <Button
                    size="sm"
                    onClick={runReplace}
                    disabled={replacing || selectedIds.size === 0}
                    data-testid="cleanup-replace-btn"
                    className="gap-1"
                  >
                    <ArrowRight size={14} />
                    {scope === 'products'
                      ? `Send ${selectedIds.size} til godkjenning`
                      : `Erstatt på ${selectedIds.size} valgte`}
                  </Button>
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {results.total === 0 ? (
              <div className="text-center py-6 text-sm text-muted-foreground">
                <CheckCircle size={32} className="mx-auto mb-2 text-success" /> Ingen treff. Alt er rent. ✨
              </div>
            ) : (
              <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
                {results.results.map((r) => {
                  const id = scope === 'terms' ? `${r.attribute_id}:${r.term_id}` : r.id;
                  const checked = selectedIds.has(id);
                  return (
                    <div key={id} className="py-2 flex items-start gap-3" data-testid={`result-${id}`}>
                      <Checkbox checked={checked} onCheckedChange={() => toggleSelect(id)} className="mt-1" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {scope === 'products' ? (
                            <>
                              <a href={r.permalink} target="_blank" rel="noreferrer" className="text-sm font-medium hover:underline">
                                {r.name}
                              </a>
                              {r.leverandor && <Badge variant="outline" className="text-xs">{r.leverandor}</Badge>}
                              {(r.categories || []).slice(0, 2).map((c) => (
                                <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>
                              ))}
                            </>
                          ) : scope === 'categories' ? (
                            <span className="text-sm font-medium">{r.name}</span>
                          ) : (
                            <span className="text-sm">
                              <strong>{r.attribute_name}</strong> / {r.term_name}
                            </span>
                          )}
                        </div>
                        {Object.entries(r.field_hits).map(([f, snip]) => (
                          <p key={f} className="text-xs text-muted-foreground mt-0.5">
                            <span className="font-mono text-[10px] uppercase mr-1.5">{f}</span>
                            {snip}
                          </p>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── PDF audit ─────────────────────────────────────────────────────
function PdfAudit({ getHeaders }) {
  const [expectedText, setExpectedText] = useState('sendes som PDF');
  const [requireVirtual, setRequireVirtual] = useState(true);
  const [filters, setFilters] = useState({ categories: [] });
  const [categoryIds, setCategoryIds] = useState([]);
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/cleanup/filters`, { headers: getHeaders() });
        setFilters(data);
      } catch { /* ignore */ }
    })();
  }, [getHeaders]);

  const toggleCategory = (cid) => {
    setCategoryIds((prev) => prev.includes(cid) ? prev.filter((x) => x !== cid) : [...prev, cid]);
  };

  const runAudit = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/api/cleanup/audit/pdf`, {
        category_ids: categoryIds.length ? categoryIds : null,
        expected_text_pattern: expectedText.trim() || null,
        require_virtual: requireVirtual,
      }, { headers: getHeaders() });
      setReport(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Audit feilet');
    } finally {
      setRunning(false);
    }
  };

  // PDF/oppskrift-categories suggested for filter
  const pdfCats = filters.categories.filter((c) => /pdf|oppskrift/i.test(c.name || ''));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><FileText size={16} /> PDF-oppskrift-audit</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Sjekker at PDF-oppskrifter har riktig boilerplate-tekst, en faktisk PDF-fil opplastet (ikke bare ekstern lenke), og at de står som virtuelle.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Forventet boilerplate-tekst (regex)</Label>
            <Input value={expectedText} onChange={(e) => setExpectedText(e.target.value)} placeholder="sendes som PDF" data-testid="pdf-text" />
            <p className="text-xs text-muted-foreground mt-1">La stå tom for å hoppe over tekst-sjekk.</p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={requireVirtual} onCheckedChange={(v) => setRequireVirtual(!!v)} />
            Krev at produktet er merket som virtuelt
          </label>
          <div>
            <Label>Begrens til kategorier (la stå tom for auto-deteksjon)</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {pdfCats.map((c) => (
                <label key={c.id} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <Checkbox checked={categoryIds.includes(c.id)} onCheckedChange={() => toggleCategory(c.id)} />
                  {c.name} ({c.count || 0})
                </label>
              ))}
              {pdfCats.length === 0 && <span className="text-xs text-muted-foreground">Ingen kategorier med "pdf"/"oppskrift" funnet — auto-detektor vil håndtere det.</span>}
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={runAudit} disabled={running} className="gap-2" data-testid="pdf-run-btn">
              <ArrowsClockwise size={14} className={running ? 'animate-spin' : ''} />
              {running ? 'Sjekker...' : 'Kjør audit'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {report && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <SummaryCard label="Mangler boilerplate-tekst" value={report.summary.missing_text} icon={Warning} color="text-warning" />
            <SummaryCard label="Mangler opplastet PDF-fil" value={report.summary.missing_file} icon={Warning} color="text-destructive" />
            <SummaryCard label="Ikke merket virtuelt" value={report.summary.not_virtual} icon={Warning} color="text-warning" />
          </div>
          <p className="text-xs text-muted-foreground">
            {report.scope} — sjekket {report.total_checked} produkter
          </p>

          <IssueList title="Mangler boilerplate-tekst" rows={report.issues.missing_text} />
          <IssueList title="Mangler opplastet PDF-fil" rows={report.issues.missing_file} showReason />
          <IssueList title="Ikke merket virtuelt" rows={report.issues.not_virtual} />
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <Icon size={28} className={color} weight="duotone" />
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="font-heading text-2xl font-bold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function IssueList({ title, rows, showReason }) {
  if (!rows || rows.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title} <Badge variant="secondary" className="ml-2">{rows.length}</Badge></CardTitle>
      </CardHeader>
      <CardContent>
        <div className="divide-y divide-border max-h-[400px] overflow-y-auto">
          {rows.map((r) => (
            <div key={r.id} className="py-2">
              <a href={r.permalink} target="_blank" rel="noreferrer" className="text-sm hover:underline">{r.name}</a>
              {showReason && r.reason && <p className="text-xs text-muted-foreground">{r.reason}</p>}
              {r.files && <p className="text-xs text-muted-foreground font-mono break-all">{(r.files || []).join(', ')}</p>}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
